import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CalendarDays,
  CalendarX2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  RotateCcw,
  SearchX,
  TriangleAlert,
} from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Skeleton } from '../../ui/Skeleton/Skeleton'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useAuth } from '../auth/AuthProvider'
import type { AppointmentItem } from '../appointments/appointments-model'
import {
  firebaseAppointmentsRepository,
  type AppointmentsRepository,
  type AppointmentWindowRepository,
} from '../appointments/appointments-repository'
import { AppointmentDetailModal } from '../appointments/components/AppointmentDetailModal'
import { ConnectedAppointmentDetailModal } from '../appointments/components/ConnectedAppointmentDetailModal'
import { useAppointmentWindow } from '../appointments/use-appointment-window'
import { useAppointmentSync } from '../appointments/use-appointment-sync'
import { useTenant } from '../tenant/TenantProvider'
import {
  calendarScheduleSettingsKey,
  SCHEDULE_SETTINGS_STALE_MS,
} from './calendar-queries'
import {
  createProviderResourceRepository,
  type ProviderResourceRepository,
} from '../settings/provider-resource-repository'
import {
  appointmentLaneKey,
  appointmentMatches,
  appointmentProviderKey,
  calendarDateKey,
  calendarRangeLabel,
  compareLanes,
  countLabel,
  formatCount,
  isCalendarDate,
  isCalendarView,
  isSameMonth,
  laneLabel,
  monthFor,
  stepCalendarDate,
  timeZoneAbbreviation,
  todayKey,
  weekFor,
  type CalendarGrouping,
  type CalendarStatusFilter,
  type CalendarView,
} from './calendar-model'
import { hoursLabel } from './calendar-layout'
import { resolveClinicDayHours } from './clinic-hours'
import { CalendarDayView } from './components/CalendarDayView'
import { CalendarMonthView } from './components/CalendarMonthView'
import { CalendarWeekView } from './components/CalendarWeekView'
import styles from './CalendarPage.module.css'

const CLINIC_LANE = '__clinic_schedule__'

const viewOptions: Array<{ value: CalendarView; label: string }> = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
]

const statusOptions: Array<{ value: CalendarStatusFilter; label: string }> = [
  { value: 'active', label: 'Active appointments' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'unknown', label: 'Needs attention' },
  { value: 'flio', label: 'Booked by Flio' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All, including cancelled' },
]

/** The range title opens the browser's own date picker to jump to a date. */
function DateJump({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (date: string) => void
}) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <span className={styles.dateJump}>
      <button
        type="button"
        className={styles.dateButton}
        aria-label={`${label}. Choose a date`}
        onClick={() => {
          const node = input.current
          if (!node) return
          try {
            if (typeof node.showPicker === 'function') {
              node.showPicker()
              return
            }
          } catch {
            // Fall through to focusing the field.
          }
          node.focus()
        }}
      >
        <CalendarDays size={16} aria-hidden="true" />
        <span>{label}</span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      <input
        ref={input}
        className={styles.dateInput}
        type="date"
        tabIndex={-1}
        aria-label="Go to date"
        value={value}
        onChange={(event) => {
          if (isCalendarDate(event.target.value)) onChange(event.target.value)
        }}
      />
    </span>
  )
}

function LoadingSchedule() {
  return (
    <div className={styles.loading} role="status" aria-busy="true">
      <span className={styles.srOnly}>Loading schedule</span>
      {Array.from({ length: 6 }, (_, index) => (
        <div className={styles.loadingRow} key={index}>
          <Skeleton width="3rem" height="0.75rem" />
          <Skeleton height={index % 2 ? '2.5rem' : '3.5rem'} />
          <Skeleton height={index % 3 ? '3.5rem' : '2rem'} />
          <Skeleton height={index % 2 ? '2rem' : '3rem'} />
        </div>
      ))}
    </div>
  )
}

export function CalendarContent({
  tenantId,
  appointmentRepository,
  providerResourceRepository,
  now = Date.now,
  initialView = 'day',
  initialDate = null,
  onStateChange,
  onRetry,
  renderAppointmentDetail,
}: {
  tenantId: string
  appointmentRepository: AppointmentsRepository & AppointmentWindowRepository
  providerResourceRepository?: ProviderResourceRepository
  now?: () => number
  initialView?: CalendarView
  initialDate?: string | null
  /** Called when the view or date changes, to keep them in the address. */
  onStateChange?: (view: CalendarView, date: string) => void
  onRetry?: () => void
  renderAppointmentDetail?: (
    appointment: AppointmentItem,
    onClose: () => void
  ) => ReactNode
}) {
  const [nowMs] = useState(() => now())
  const [view, setView] = useState<CalendarView>(initialView)
  const [anchor, setAnchor] = useState(() => initialDate ?? todayKey(nowMs))
  const [providerFilter, setProviderFilter] = useState('all')
  const [statusFilter, setStatusFilter] =
    useState<CalendarStatusFilter>('active')
  const [grouping, setGrouping] = useState<CalendarGrouping>('provider')
  const [focusLane, setFocusLane] = useState<string | null>(null)
  const [selected, setSelected] = useState<AppointmentItem | null>(null)
  const followsToday = useRef(initialDate === null)

  const rangeDays = useMemo(
    () =>
      view === 'day'
        ? [anchor]
        : view === 'month'
          ? monthFor(anchor)
          : weekFor(anchor),
    [anchor, view]
  )
  const schedule = useAppointmentWindow(
    appointmentRepository,
    tenantId,
    rangeDays
  )
  const loaded = schedule.appointments
  const clinicTimeZone = schedule.timeZone
  const currentClinicDay = todayKey(nowMs, clinicTimeZone)
  const syncNotice = useAppointmentSync(
    appointmentRepository,
    tenantId,
    rangeDays,
    clinicTimeZone,
    nowMs
  )

  // A visit shows the hours it read last time at once instead of clearing them
  // and asking again (6–8 s while RTDB was slow on 2026-10-21).
  // provider-resources is the query Settings itself reads and refreshes.
  const resources = useQuery({
    queryKey: ['settings', tenantId, 'provider-resources'],
    queryFn: () => providerResourceRepository!.getResources(tenantId),
    enabled: Boolean(providerResourceRepository),
    staleTime: SCHEDULE_SETTINGS_STALE_MS,
  })
  const legacySchedule =
    !providerResourceRepository ||
    resources.data?.authorityMode === 'TENANT_SECRET'
  const legacyScheduleSettings = useQuery({
    queryKey: calendarScheduleSettingsKey(tenantId),
    queryFn: () => appointmentRepository.getClinicScheduleSettings(tenantId),
    enabled: legacySchedule,
    staleTime: SCHEDULE_SETTINGS_STALE_MS,
  })
  const clinicScheduleSettings = useMemo((): unknown => {
    if (legacySchedule) return legacyScheduleSettings.data ?? null
    const providerResources = resources.data?.providerResources
    if (!resources.data) return null
    return {
      effectiveSchedulingHours:
        providerResources?.freshness === 'CURRENT'
          ? providerResources.effectiveSchedulingHours
          : undefined,
    }
  }, [legacySchedule, legacyScheduleSettings.data, resources.data])

  // Until someone navigates, "today" is the clinic's today, not the browser's.
  useEffect(() => {
    if (followsToday.current) setAnchor(currentClinicDay)
  }, [currentClinicDay])

  useEffect(() => {
    onStateChange?.(view, anchor)
  }, [anchor, onStateChange, view])

  const goTo = (
    date: string,
    nextView: CalendarView = view,
    lane: string | null = null
  ) => {
    followsToday.current = false
    setAnchor(date)
    setView(nextView)
    setFocusLane(lane)
  }

  const providers = useMemo(
    () =>
      Array.from(new Set(loaded.map(appointmentProviderKey))).sort(
        compareLanes
      ),
    [loaded]
  )
  const providerMatched = useMemo(
    () =>
      loaded.filter((item) => appointmentMatches(item, providerFilter, 'all')),
    [loaded, providerFilter]
  )
  const filtered = useMemo(
    () =>
      providerMatched.filter((item) =>
        appointmentMatches(item, 'all', statusFilter)
      ),
    [providerMatched, statusFilter]
  )
  const appointmentsByDate = useMemo(() => {
    const result = new Map<string, AppointmentItem[]>()
    for (const item of filtered) {
      if (item.scheduledAtMs === null) continue
      const date = calendarDateKey(item.scheduledAtMs, item.clinicTimeZone)
      result.set(date, [...(result.get(date) ?? []), item])
    }
    return result
  }, [filtered])

  // The summary speaks for the period named in the title: the month view's
  // leading and trailing days belong to other months.
  const summaryDays = useMemo(
    () =>
      new Set(
        view === 'month'
          ? rangeDays.filter((date) => isSameMonth(date, anchor))
          : rangeDays
      ),
    [anchor, rangeDays, view]
  )
  const inSummary = (item: AppointmentItem) =>
    item.scheduledAtMs !== null &&
    summaryDays.has(calendarDateKey(item.scheduledAtMs, item.clinicTimeZone))
  const shownInRange = filtered.filter(inSummary)
  const providerInRange = providerMatched.filter(inSummary)
  const shownConfirmed = shownInRange.filter(
    (item) => item.status === 'confirmed'
  ).length
  const hiddenCancelled =
    statusFilter === 'active'
      ? providerInRange.filter((item) => item.status === 'cancelled').length
      : 0
  const filtersActive = providerFilter !== 'all' || statusFilter !== 'active'

  const dayAppointments = useMemo(
    () => appointmentsByDate.get(anchor) ?? [],
    [anchor, appointmentsByDate]
  )
  const dayLanes = useMemo(() => {
    const keys = Array.from(
      new Set(dayAppointments.map((item) => appointmentLaneKey(item, grouping)))
    ).sort(compareLanes)
    if (keys.length) return keys
    return [
      grouping === 'provider' && providerFilter !== 'all'
        ? providerFilter
        : CLINIC_LANE,
    ]
  }, [dayAppointments, grouping, providerFilter])
  const dayHours = useMemo(
    () => resolveClinicDayHours(clinicScheduleSettings, anchor, clinicTimeZone),
    [anchor, clinicScheduleSettings, clinicTimeZone]
  )
  const status = schedule.status
  const clearFilters = () => {
    setProviderFilter('all')
    setStatusFilter('active')
  }

  const dayEmpty =
    view === 'day' && status === 'ready' && dayAppointments.length === 0 ? (
      <div className={styles.emptyCard}>
        <span className={styles.emptyIcon} aria-hidden="true">
          {providerInRange.length ? (
            <SearchX size={20} />
          ) : (
            <CalendarX2 size={20} />
          )}
        </span>
        <div>
          <strong>
            {providerInRange.length && filtersActive
              ? 'No appointments match these filters'
              : providerInRange.length
                ? 'Only cancelled appointments on this day'
                : dayHours.status === 'closed'
                  ? 'Closed for scheduling'
                  : 'No appointments on this day'}
          </strong>
          {providerInRange.length && filtersActive ? (
            <Button type="button" variant="ghost" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : null}
        </div>
      </div>
    ) : null

  return (
    <section className={styles.panel} aria-label="Calendar">
      <div className={styles.toolbar}>
        <div className={styles.navigation}>
          <Button
            type="button"
            variant="secondary"
            className={styles.todayButton}
            onClick={() => {
              goTo(currentClinicDay)
              followsToday.current = true
            }}
          >
            Today
          </Button>
          <span className={styles.stepper}>
            <button
              type="button"
              aria-label="Previous date range"
              onClick={() => goTo(stepCalendarDate(anchor, view, -1))}
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Next date range"
              onClick={() => goTo(stepCalendarDate(anchor, view, 1))}
            >
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </span>
          <DateJump
            label={calendarRangeLabel(anchor, view)}
            value={anchor}
            onChange={(date) => goTo(date)}
          />
        </div>
        <div
          className={styles.segmented}
          role="group"
          aria-label="Calendar view"
        >
          {viewOptions.map((option) => (
            <button
              type="button"
              key={option.value}
              aria-pressed={view === option.value}
              onClick={() => goTo(anchor, option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.filters}>
        <select
          aria-label="Filter calendar provider"
          value={providerFilter}
          onChange={(event) => setProviderFilter(event.target.value)}
        >
          <option value="all">All providers</option>
          {providers.map((provider) => (
            <option value={provider} key={provider}>
              {laneLabel(provider)}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter calendar status"
          value={statusFilter}
          onChange={(event) =>
            setStatusFilter(event.target.value as CalendarStatusFilter)
          }
        >
          {statusOptions.map((option) => (
            <option value={option.value} key={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {view !== 'month' ? (
          <div
            className={`${styles.segmented} ${styles.grouping}`}
            role="group"
            aria-label="Group by"
          >
            {(['provider', 'room'] as const).map((value) => (
              <button
                type="button"
                key={value}
                aria-pressed={grouping === value}
                onClick={() => {
                  setGrouping(value)
                  setFocusLane(null)
                }}
              >
                {value === 'provider' ? 'Providers' : 'Rooms'}
              </button>
            ))}
          </div>
        ) : null}
        {filtersActive ? (
          <Button
            type="button"
            variant="ghost"
            className={styles.clearFilters}
            onClick={clearFilters}
          >
            Clear filters
          </Button>
        ) : null}
        {status === 'ready' ? (
          <p className={styles.summary}>
            {view === 'day' && dayHours.status !== 'fallback' ? (
              <span className={styles.hours} title="Hours Flio can schedule">
                <Clock3 size={14} aria-hidden="true" />
                {dayHours.status === 'closed'
                  ? 'Closed'
                  : `Hours ${hoursLabel(dayHours.intervals)}`}
              </span>
            ) : null}
            <span>{countLabel(shownInRange.length, 'appointment')}</span>
            <span>{formatCount(shownConfirmed)} confirmed</span>
            {hiddenCancelled ? (
              <button
                type="button"
                className={styles.showCancelled}
                onClick={() => setStatusFilter('all')}
              >
                Show {hiddenCancelled} cancelled
              </button>
            ) : null}
          </p>
        ) : null}
      </div>

      {syncNotice?.tone === 'warning' ? (
        <p className={styles.notice} role="status">
          <TriangleAlert size={15} aria-hidden="true" />
          <span>{syncNotice.message}</span>
        </p>
      ) : null}

      {status === 'loading' ? (
        <LoadingSchedule />
      ) : status === 'forbidden' ? (
        <div className={styles.state}>
          <StatePanel
            kind="forbidden"
            title="Calendar unavailable"
            description="You don’t have access to this clinic’s schedule."
          />
        </div>
      ) : status !== 'ready' ? (
        <div className={styles.state}>
          <StatePanel
            kind="error"
            title="Calendar unavailable"
            description="The schedule couldn’t be loaded."
          />
          {onRetry ? (
            <Button type="button" variant="secondary" onClick={onRetry}>
              <RotateCcw size={15} aria-hidden="true" /> Try again
            </Button>
          ) : null}
        </div>
      ) : view === 'day' ? (
        <CalendarDayView
          date={anchor}
          appointments={dayAppointments}
          lanes={dayLanes}
          grouping={grouping}
          dayHours={dayHours}
          timeZone={clinicTimeZone}
          timeZoneLabel={timeZoneAbbreviation(nowMs, clinicTimeZone)}
          isToday={anchor === currentClinicDay}
          now={now}
          focusLane={focusLane}
          empty={dayEmpty}
          onSelectAppointment={setSelected}
        />
      ) : view === 'week' ? (
        <CalendarWeekView
          days={weekFor(anchor)}
          appointmentsByDate={appointmentsByDate}
          grouping={grouping}
          today={currentClinicDay}
          onOpenDay={(date, lane) => goTo(date, 'day', lane ?? null)}
        />
      ) : (
        <CalendarMonthView
          days={monthFor(anchor)}
          anchor={anchor}
          today={currentClinicDay}
          appointmentsByDate={appointmentsByDate}
          onOpenDay={(date) => goTo(date, 'day')}
        />
      )}

      {selected
        ? (renderAppointmentDetail?.(selected, () => setSelected(null)) ?? (
            <AppointmentDetailModal
              appointment={selected}
              tenantId={tenantId}
              nowMs={nowMs}
              onClose={() => setSelected(null)}
            />
          ))
        : null}
    </section>
  )
}

export function CalendarPage({
  appointmentRepository = firebaseAppointmentsRepository,
  providerResourceRepository,
}: {
  appointmentRepository?: AppointmentsRepository & AppointmentWindowRepository
  providerResourceRepository?: ProviderResourceRepository
}) {
  const auth = useAuth()
  const tenant = useTenant()
  const [searchParams, setSearchParams] = useSearchParams()
  const [attempt, setAttempt] = useState(0)
  const currentUser = auth.status === 'authenticated' ? auth.user : null
  const scheduleSettings = useMemo(
    () =>
      providerResourceRepository ??
      createProviderResourceRepository({ getUser: () => currentUser }),
    [currentUser, providerResourceRepository]
  )
  const requestedView = searchParams.get('view')
  const requestedDate = searchParams.get('date')
  // Read once per mount: the address follows the page, never the reverse.
  const [initial] = useState(() => ({
    view: isCalendarView(requestedView) ? requestedView : 'day',
    date: isCalendarDate(requestedDate) ? requestedDate : null,
  }))
  const tenantId = tenant.selectedClinic?.id ?? ''
  // setSearchParams changes identity with every address change; keep the
  // callback stable and write only when the view or date actually differs.
  const setParams = useRef(setSearchParams)
  setParams.current = setSearchParams
  const onStateChange = useCallback((view: CalendarView, date: string) => {
    setParams.current(
      (current) => {
        if (current.get('view') === view && current.get('date') === date) {
          return current
        }
        const next = new URLSearchParams(current)
        next.set('view', view)
        next.set('date', date)
        return next
      },
      { replace: true }
    )
  }, [])
  if (!tenant.selectedClinic) return null
  return (
    <CalendarContent
      key={`${tenantId}:${attempt}`}
      tenantId={tenantId}
      appointmentRepository={appointmentRepository}
      providerResourceRepository={scheduleSettings}
      initialView={initial.view}
      initialDate={initial.date}
      onStateChange={onStateChange}
      onRetry={() => setAttempt((value) => value + 1)}
      renderAppointmentDetail={(appointment, onClose) => (
        <ConnectedAppointmentDetailModal
          key={appointment.appointmentId}
          appointment={appointment}
          tenantId={tenantId}
          nowMs={Date.now()}
          onClose={onClose}
        />
      )}
    />
  )
}
